import { useState } from 'react';
import { api } from '../lib/api';

/**
 * M21 T5c: billing step — shown BEFORE Stripe Connect when the tenant has no
 * billing subscription (billingRequired === true from /onboarding/status).
 *
 * Creates a Stripe Checkout Session via POST /api/billing/checkout (platform
 * account, 30-day trial; price + card policy shown by Stripe itself — copy
 * here intentionally avoids both) and redirects the browser. On return, the
 * billing webhook has already flipped billing_status to 'trialing', so the
 * wizard's status refresh sees billingRequired=false and advances.
 *
 * Reference: src/handlers/billing.ts (handleBillingCheckout)
 *            M21_PLAN.md T5c
 */
export function StepBilling() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startTrial = async () => {
    setLoading(true);
    setError(null);
    try {
      const { url } = await api.post<{ url: string; }>('billing/checkout');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
      <div className="mx-auto max-w-md text-center">
        <h2 className="text-xl font-semibold text-slate-900">
          Start your 30-day free trial
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Stripe Sync by AmpDeck brings your Stripe billing inside HubSpot —
          enrich your CRM with live revenue data and manage subscriptions
          without ever leaving HubSpot.
        </p>

        <div className="mt-6 rounded-lg bg-slate-50 p-4 text-left">
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <span className="text-emerald-600">✓</span>
              Customers, subscriptions and products mirrored in HubSpot
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600">✓</span>
              Sidebar cards with MRR, lifetime value, invoices and disputes
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600">✓</span>
              Pause, cancel or refund a subscription right from the record
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600">✓</span>
              Native HubSpot reporting on synced custom properties
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600">✓</span>
              B2B company rollups + multi-user role-based access
            </li>
          </ul>
        </div>

        <button
          onClick={startTrial}
          disabled={loading}
          className="mt-6 w-full rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Redirecting…' : 'Start free trial'}
        </button>

        {error && (
          <p className="mt-3 text-sm text-red-600">{error}</p>
        )}

        <p className="mt-4 text-xs text-slate-400">
          Nothing is charged during the trial — plan details are shown on the
          next screen. Cancel any time via the Stripe Customer Portal.
        </p>
      </div>
    </div>
  );
}
